import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import PageLayout from "../../layouts/PageLayout";

const apiUrl = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
});
const unwrapList = (response) => {
    const data = response?.data?.data ?? response?.data;
    return Array.isArray(data) ? data : [];
};

const imageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const baseUrl = process.env.REACT_APP_IMG_URL || "";
    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

export default function StoreSellerShipmentsPage() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [warehouseEnabled, setWarehouseEnabled] = useState(false);
    const [products, setProducts] = useState([]);
    const [couriers, setCouriers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [courierId, setCourierId] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [proofImage, setProofImage] = useState(null);

    const loadPage = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [preferenceResponse, shipmentResponse, courierResponse] = await Promise.all([
                axios.get(`${apiUrl}/store-seller/storage-preference`, { headers: authHeaders() }),
                axios.get(`${apiUrl}/store-seller/shipments`, { headers: authHeaders() }),
                axios.get(`${apiUrl}/store-seller/courier-companies`, { headers: authHeaders() }),
            ]);

            const warehouse = preferenceResponse.data?.storage_preference === "admin_store";
            setWarehouseEnabled(warehouse);
            setCouriers(unwrapList(courierResponse));

            if (warehouse) {
                const productResponse = await axios.get(
                    `${apiUrl}/store-seller/shipments/eligible-products`,
                    { headers: authHeaders() }
                );
                setProducts(unwrapList(productResponse));
            } else {
                setProducts([]);
            }
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Could not load shipment information.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPage();
    }, [loadPage]);

    const toggleProduct = (productId) => {
        setSelectedIds((current) => current.includes(productId)
            ? current.filter((id) => id !== productId)
            : [...current, productId]);
    };

    const createShipment = async (event) => {
        event.preventDefault();
        setError("");
        setSuccess("");
        if (!selectedIds.length) {
            setError("Select at least one eligible product to ship.");
            return;
        }

        const payload = new FormData();
        if (courierId) payload.append("courier_company_id", courierId);
        if (trackingNumber) payload.append("tracking_number", trackingNumber);
        if (proofImage) payload.append("dispatched_image", proofImage);
        selectedIds.forEach((id) => payload.append("product_ids[]", id));

        setSubmitting(true);
        try {
            const response = await axios.post(`${apiUrl}/store-seller/shipments`, payload, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            setSuccess(response.data?.message || "Shipment created successfully.");
            setSelectedIds([]);
            setCourierId("");
            setTrackingNumber("");
            setProofImage(null);
            await loadPage();
        } catch (requestError) {
            const validationMessages = requestError.response?.data?.errors
                ? Object.values(requestError.response.data.errors).flat().join(" ")
                : "";
            setError(validationMessages || requestError.response?.data?.message || "Could not create shipment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">Create Store Shipment</h3>
                        <p className="text-muted mb-0">Fill out shipment details and select products to dispatch to the admin warehouse.</p>
                    </div>
                    <Button as={Link} to="/seller-store-shipments" variant="secondary">View shipments</Button>
                </div>
            </div>
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    {!warehouseEnabled && (
                        <Alert variant="info">
                            Product shipping is available only when your storage preference is the admin warehouse.
                        </Alert>
                    )}
                    {warehouseEnabled && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <Form onSubmit={createShipment}>
                                    <Row className="g-3 mb-4">
                                        <Col md={4}>
                                            <Form.Label>Courier company</Form.Label>
                                            <Form.Select value={courierId} onChange={(event) => setCourierId(event.target.value)}>
                                                <option value="">Select courier</option>
                                                {couriers.map((courier) => (
                                                    <option key={courier.id} value={courier.id}>
                                                        {courier.company_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Label>Tracking number</Form.Label>
                                            <Form.Control value={trackingNumber} onChange={(event) => setTrackingNumber(event.target.value)} placeholder="Enter tracking number" />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Label>Dispatch proof (optional)</Form.Label>
                                            <Form.Control type="file" accept="image/*" onChange={(event) => setProofImage(event.target.files?.[0] || null)} />
                                        </Col>
                                    </Row>

                                    <h5 className="mb-3">Select Products for Shipment</h5>
                                    {products.length === 0 ? (
                                        <Alert variant="info" className="mb-4">
                                            No checked products are currently awaiting shipment.
                                        </Alert>
                                    ) : (
                                        <Row className="g-3 mb-4">
                                            {products.map((product) => {
                                                const isSelected = selectedIds.includes(product.id);
                                                const imgSource = imageUrl(product.card_img);
                                                return (
                                                    <Col key={product.id} md={6} lg={4}>
                                                        <Card
                                                            className={`h-100 cursor-pointer border ${isSelected ? "border-primary bg-light" : "border-muted"}`}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => toggleProduct(product.id)}
                                                        >
                                                            <Card.Body className="d-flex align-items-center gap-3 p-3">
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    id={`shipment-product-${product.id}`}
                                                                    checked={isSelected}
                                                                    onChange={() => toggleProduct(product.id)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                {imgSource ? (
                                                                    <img src={imgSource} alt="" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 6 }} />
                                                                ) : (
                                                                    <div className="bg-secondary rounded d-flex align-items-center justify-content-center text-white" style={{ width: 50, height: 50, fontSize: 12 }}>
                                                                        No Img
                                                                    </div>
                                                                )}
                                                                <div className="overflow-hidden">
                                                                    <small className="text-muted d-block">{product.product_code || "—"}</small>
                                                                    <span className="fw-semibold text-truncate d-block" style={{ fontSize: "0.9rem" }}>
                                                                        {product.title || "Product"}
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    )}

                                    <Button type="submit" disabled={submitting || products.length === 0}>
                                        {submitting ? "Submitting..." : `Create shipment (${selectedIds.length})`}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}
        </PageLayout>
    );
}